import React, { useEffect, useRef, useState } from "react"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"

gsap.registerPlugin(ScrollTrigger)

// Queries against the GtR API (https://gtr.ukri.org/resources/api.html).
// Each is a broad keyword; we filter client-side by status + title heuristics.
const QUERIES = [
  "%22Accelerator+Programme%22",
  "ICURe",
  "Catapult",
  "%22Innovation+Launchpad%22",
  "%22Launchpad+Cluster%22",
  "%22Innovation+Accelerator%22",
  "%22Incubator+Programme%22",
]

// Title must match at least one of these (accelerator/enabler programme signals).
const TITLE_ALLOW = /accelerator|catapult|icure|launchpad|incubator|innovation cluster|cluster management|cluster innovation|venture creation/i

// Title is rejected if it matches any of these (physics research, fellowships, etc).
const TITLE_DENY = /accelerator science|accelerator technology|accelerator driven|accelerator beam|accelerator based|plasma|particle|laser|electron|proton|graviton|gravimeter|fellowship|parliament|biomimetic|soil|ocean|pycnocline/i

// Only show programmes funded under innovation/enabler remits.
const FUNDER_ALLOW = new Set(["Innovate UK", "EPSRC", "ISCF"])

function decodeEntities(s) {
  if (!s) return ""
  return s
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
}

async function fetchQuery(q) {
  const url = `https://gtr.ukri.org/gtr/api/projects?q=${q}&p=1&s=100`
  const res = await fetch(url, { headers: { Accept: "application/json" } })
  if (!res.ok) return []
  const data = await res.json()
  return data.project || []
}

async function fetchPerson(id) {
  const url = `https://gtr.ukri.org/gtr/api/persons/${id}`
  const res = await fetch(url, { headers: { Accept: "application/json" } })
  if (!res.ok) return null
  return res.json()
}

function pmPersonId(p) {
  const links = (p.links && p.links.link) || []
  const pm = links.find(l => l.rel === "PM_PER")
  if (!pm || !pm.href) return null
  const m = pm.href.match(/persons?\/([A-F0-9-]+)/i)
  return m ? m[1] : null
}

function normalise(projects) {
  const now = new Date()
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime()
  const byId = new Map()
  for (const p of projects) {
    if (p.status !== "Active") continue
    const title = p.title || ""
    if (!TITLE_ALLOW.test(title)) continue
    if (TITLE_DENY.test(title)) continue
    if (!FUNDER_ALLOW.has(p.leadFunder)) continue
    const { end } = fundDates(p)
    if (end && end < startOfMonth) continue
    if (!byId.has(p.id)) byId.set(p.id, p)
  }
  const out = Array.from(byId.values())
  out.sort((a, b) => {
    const f = (a.leadFunder || "").localeCompare(b.leadFunder || "")
    return f || (a.title || "").localeCompare(b.title || "")
  })
  return out
}

function leadOrg(p) {
  const participants = (p.participantValues && p.participantValues.participant) || []
  const lead = participants.find(x => x.role === "LEAD_PARTICIPANT")
  return (lead && lead.organisationName) || "—"
}

function totalGrant(p) {
  const participants = (p.participantValues && p.participantValues.participant) || []
  return participants.reduce((sum, x) => sum + (x.grantOffer || 0), 0)
}

function formatGBP(n) {
  if (!n) return "—"
  if (n >= 1e9) return `£${(n / 1e9).toFixed(1)}bn`
  if (n >= 1e6) return `£${(n / 1e6).toFixed(n >= 1e7 ? 0 : 1)}m`
  if (n >= 1e3) return `£${Math.round(n / 1e3)}k`
  return `£${Math.round(n)}`
}

function fundDates(p) {
  const links = (p.links && p.links.link) || []
  const fund = links.find(l => l.rel === "FUND")
  if (!fund) return { start: null, end: null }
  return { start: fund.start || null, end: fund.end || null }
}

function formatRange(start, end) {
  const fmt = ms => {
    if (!ms) return null
    const d = new Date(ms)
    return `${d.toLocaleString("en-GB", { month: "short" })} ${d.getFullYear()}`
  }
  const a = fmt(start)
  const b = fmt(end)
  if (!a && !b) return "—"
  if (a && b) return `${a} – ${b}`
  return a || b
}

function gtrUrl(p) {
  const ids = (p.identifiers && p.identifiers.identifier) || []
  const rcuk = ids.find(i => i.type === "RCUK")
  if (rcuk && rcuk.value) return `https://gtr.ukri.org/projects?ref=${rcuk.value}`
  return p.href || "https://gtr.ukri.org"
}

export function Enablers() {
  const [programmes, setProgrammes] = useState([])
  const [people, setPeople] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const rowRefs = useRef([])

  useEffect(() => {
    document.body.style.backgroundColor = ""
    document.body.style.color = ""

    let cancelled = false
    ;(async () => {
      try {
        const results = await Promise.all(QUERIES.map(fetchQuery))
        if (cancelled) return
        const flat = results.reduce((acc, arr) => acc.concat(arr), [])
        const filtered = normalise(flat)
        setProgrammes(filtered)
        setLoading(false)

        // Fetch project lead contacts in the background.
        const ids = Array.from(
          new Set(filtered.map(pmPersonId).filter(Boolean))
        )
        const fetched = await Promise.all(ids.map(fetchPerson))
        if (cancelled) return
        const map = {}
        ids.forEach((id, i) => { if (fetched[i]) map[id] = fetched[i] })
        setPeople(map)
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load")
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (loading) return
    const ctx = gsap.context(() => {
      rowRefs.current.forEach(row => {
        if (!row) return
        gsap.fromTo(
          row,
          { opacity: 0, y: 16 },
          {
            opacity: 1,
            y: 0,
            duration: 0.5,
            ease: "power3.out",
            scrollTrigger: { trigger: row, start: "top 94%" },
          }
        )
      })
      ScrollTrigger.refresh()
    })
    return () => ctx.revert()
  }, [loading, programmes])

  return (
    <div className="enablers-page">
      <div className="services-header">
        <p className="eyebrow">Accelerator programmes & enablers</p>
        <h1 className="services-headline">
          Active UK innovation<br />accelerators & enablers.
        </h1>
        <p className="enablers-subtext">
          A live view of UKRI-funded accelerator programmes, Catapult centres, ICURe venture
          creation and innovation cluster launchpads that are currently active. Data pulled on
          page load from the{" "}
          <a
            className="enablers-link"
            href="https://gtr.ukri.org/resources/api.html"
            target="_blank"
            rel="noreferrer"
          >
            Gateway to Research API
          </a>
          .
        </p>
      </div>

      <div className="enablers-table">
        <div className="enablers-header-row">
          <span>Programme</span>
          <span className="enablers-col-size">Size</span>
          <span>Dates</span>
          <span className="enablers-col-funder">Funder</span>
        </div>

        {loading && (
          <div className="enablers-status">Loading active programmes from gtr.ukri.org…</div>
        )}

        {!loading && error && (
          <div className="enablers-status">Couldn’t reach the GtR API ({error}).</div>
        )}

        {!loading && !error && programmes.length === 0 && (
          <div className="enablers-status">No active programmes matched.</div>
        )}

        {!loading && programmes.map((p, i) => {
          const { start, end } = fundDates(p)
          const pid = pmPersonId(p)
          const person = pid && people[pid]
          const org = decodeEntities(leadOrg(p))
          const contactName = person
            ? [person.firstName, person.surname].filter(Boolean).join(" ")
            : null
          const searchUrl = contactName
            ? `https://www.google.com/search?q=${encodeURIComponent(`"${contactName}" ${leadOrg(p)} LinkedIn`)}`
            : null
          return (
            <a
              key={p.id}
              ref={el => (rowRefs.current[i] = el)}
              className="enabler-row"
              href={gtrUrl(p)}
              target="_blank"
              rel="noreferrer"
            >
              <span className="enabler-primary">
                <span className="enabler-title">{decodeEntities(p.title)}</span>
                <span className="enabler-org">{org}</span>
                {contactName && (
                  <span
                    className="enabler-contact"
                    onClick={e => { e.stopPropagation(); e.preventDefault(); window.open(searchUrl, "_blank", "noreferrer") }}
                  >
                    Lead: {contactName} ↗
                  </span>
                )}
              </span>
              <span className="enabler-size">{formatGBP(totalGrant(p))}</span>
              <span className="enabler-dates">{formatRange(start, end)}</span>
              <span className="enabler-funder">{p.leadFunder}</span>
            </a>
          )
        })}

        {!loading && programmes.length > 0 && (
          <div className="enablers-footnote">
            {programmes.length} active programmes. Source:{" "}
            <a
              className="enablers-link"
              href="https://gtr.ukri.org"
              target="_blank"
              rel="noreferrer"
            >
              gtr.ukri.org
            </a>
            .
          </div>
        )}
      </div>
    </div>
  )
}
